import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, replace, cloneDeep, isNull, isUndefined } from "lodash";

import Box from "@mui/material/Box";

import unitPriceService from "@plannel/services/data/unit-price-service";
import customerService from "@plannel/services/data/customer-service";
import GridDatePicker from "@plannel/components/GridDatePicker"
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import PaginationContainer from "@plannel/components/PaginationContainer";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";

import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import NumberUtil from "@plannel/utils/number-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const CURRENT_DATE = new ZDate();
const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() - 5, 1);
const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
const INIT_DATE = { start: START_DATE, end: END_DATE };

const { snackBarMessageColor } = ColorUtil;
const UnitPrice = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [sortParams, setSortParams] = useState({});
  const [period, setPeriod] = useState(reduxViewState?.start && reduxViewState?.end ? { start: reduxViewState.start, end: reduxViewState.end } : INIT_DATE);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [datas, setDatas] = useState([]);

  const [csvColDef, setCsvColDef] = useState([]);
  const EXCEL_EXPORT = "excelExport";
  const userData = JSON.parse(localStorage.getItem("user"));

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dropdownValueFormatter = params => {
    return params.value ? params.value.name : "";
  }

  const setItemCode = params => {
    params.data.item = { itemCd: params.newValue, name: "" };
    return true;
  }
  
  const currencyRenderer = (param, isExport = false) => {
    let symbol = userData?.currencySymbol;
    const currencyCd = param?.data?.customer?.currencyCd;
    if (currencyCd) {
      symbol = CodeUtil.getCurrencySymbol(currencyCd);
    } 
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }

    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const columnDefs = [
    {
      headerName: "itemCd", field: "item.itemCd", colId: "itemCd", minWidth: 150, flex: 2, cellClass: 'stringType',
      valueSetter: setItemCode, filterType: 'string'
    },
    { headerName: "itemName", field: "item.name", colId: "itemName", type: ["nonEditableColumn"], minWidth: 200, flex: 4, filterType: 'string' },
    { headerName: "customerCd", field: "customer.customerCd", colId: "customerCd", type: ["nonEditableColumn"], minWidth: 150, flex: 2, cellClass: 'stringType', filterType: 'string' },
    {
      headerName: "customerName", field: "customer", colId: "customerName", minWidth: 150, flex: 2,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      cellEditorParams: { values: customers },
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      comparator: (value1, value2, nodeA, nodeB, isDescending) => {
        const val1 = !value1 ? "" : value1.name;
        const val2 = !value2 ? "" : value2.name;
        
        return nullSafeComparator(val1, val2, isDescending);
      },
      filterType: 'string',
    },
    {
      headerName: "baseDt", field: "baseDate", width: 120,
      type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "month", isFirstDay: true },
      valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM"), filterType: 'date', filterUnit: 'month'
    },
    { headerName: "unitPrice", field: "unitPrice", type: ["decimalPointColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 150, filterType: 'number' },
    { headerName: "currencyCd", field: "customer.currencyCd", colId: "currencyCd", type: ["nonEditableColumn"], width: 150, filterType: 'string' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 4, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 4, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {};

    if (period?.start && period?.end) {
      searchFilters.startDate = period.start;
      searchFilters.endDate = period.end;
    }

    if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }
    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    }

    if (customerSearch?.current) {
      searchFilters.customer = customerSearch.current;
    }
    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  };

  const retrieveUnitPrice = () => {
    const params = getRequestParams();
    unitPriceService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setDatas(results);
        setGridPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        DataState.initialize(gridRef.current?.api);

        const filterValue = { ...period, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, currentPage, pageSize, advancedFilters };
        const reduxState = reduxUtil.createObjectState(viewName, filterValue);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      });
  };

  const removeUnitPrice = () => {
    gridRef.current.api.stopEditing();

    const deleteIds = [];
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        deleteIds.push(data.id);
      }
    );

    if (deleteIds.length > 0) {
      unitPriceService.remove(deleteIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveUnitPrice();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const isInvalid = datas => {
    for (const data of datas) {
      const { item, customer, baseDate, unitPrice } = data;
      const emptyValues = [];
      if (!item?.itemCd) {
        emptyValues.push(t("itemCd"));
      }
      if (!customer) {
        emptyValues.push(t("customer"));
      }
      if (!baseDate) {
        emptyValues.push(t("baseDt"));
      }
      if (!unitPrice) {
        emptyValues.push(t("unitPrice"));
      }

      if (!isEmpty(emptyValues)) {
        const values = emptyValues.join(",");
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: `[${values}]` }) });
        return true;
      }
    }

    return false;
  }

  const saveUnitPrice = () => {
    gridRef.current.api.stopEditing();

    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (isInvalid(allStateData)) {
      return;
    }

    if (allStateData.length > 0) {
      unitPriceService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .then(() => {
          retrieveUnitPrice();
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const retrieveCustomers = () => {
    customerService.getLookup()
      .then(response => {
        const { results } = response.data;
        setCustomers(results);
      })
      .catch(e => {
        debug(e);
      })
  };

  const onGridReady = params => {
    retrieveCustomers();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    setCsvColDef(cloneDeep(columnDefs))
  }

  const addRow = () => {
    const newRow = { id: null, customerCd: "" };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveUnitPrice();
    } else if (action === "DELETE") {
      removeUnitPrice();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveUnitPrice();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading && period) {
      DataState.initialize(gridRef.current?.api);
      retrieveUnitPrice();
    }

    return () => {
      setDatas([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (!isEmpty(customers)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  useEffect(() => {
    if (csvColDef.isEmpty) {
      return;
    }
    for (const col of csvColDef) {
      if (col.valueFormatter?.customType === EXCEL_EXPORT) {
        col.cellRenderer = null;
        col.valueFormatter = null;
        col.valueGetter = params => currencyRenderer(params, true);
        col.cellClass = params => {
          const value = Number(params.value);
          return !isNaN(value) && !Number.isInteger(value) ? 'flexibleDecimalType' : '';
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvColDef])

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setDatas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <Box sx={{ position: "relative", display: "flex" }}>
          <PeriodFilter period={period} setPeriod={setPeriod} dateType="month" />
        </Box>
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} sx={{ mr: 1 }} extraCodesRef={extraItemCodesRef} />
        <CustomerAutocomplete retrieve={initPageToRetrieve} useAccessFilter={false} searchValue={customerSearch} extraCodesRef={extraCustomerCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={unitPriceService} columnDefs={csvColDef} params={getRequestParams(false)} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="unit-price-grid" className="ag-theme-balham grid-area" sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={datas}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              enterNavigatesVertically
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={gridPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(UnitPrice);